<script setup>

/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { defineProps, defineEmits, computed, ref, watch, useSlots } from 'vue';

/*==================================================================*/
/*================== IMPORTS DE COMPONENTES ========================*/
/*==================================================================*/

import HeaderDaTabela from './HeaderDaTabela/HeaderDaTabela.vue';
import BodyDaTabela from './BodyDaTabela/BodyDaTabela.vue';
import FooterDaTabela from './FooterDaTabela/FooterDaTabela.vue';
import UtilArray from '@/utils/UtilArray.mjs';

/*==================================================================*/
/*================== PROPS =========================================*/
/*==================================================================*/

const props = defineProps({
    headers: {
        type: Array,
        default: () => []
    },
    dados: {
        type: Array,
        default: () => []
    },
    disabled: {
        type: Boolean,
        default: false
    },
    selecao: {
        type: String,
        default: 'nenhum'
    },
    selecionados: {
        type: Array,
        default: () => []
    },
    selecionado: {
        type: Object,
        default: () => ({})
    },
    itemKey: {
        type: String,
        default: 'id'
    },
    paginacao: {
        type: String,
        default: 'nenhuma',
        validator: (valor) => UtilArray.includes(['nenhuma', 'paginada', 'virtual'], valor)
    },
    itensPorPagina: {
        type: Number,
        default: 10
    }
});

const emits = defineEmits([
    'update:selecionados',
    'update:selecionado',
]);

/*==================================================================*/
/*================== PAGINACAO =====================================*/
/*==================================================================*/

const paginaAtual = ref(1);

const totalPaginas = computed(() => {

    return Math.max(1, Math.ceil(props.dados.length / props.itensPorPagina));

});

watch(totalPaginas, (novoTotal) => {

    if(paginaAtual.value > novoTotal){
        paginaAtual.value = novoTotal;
    }

});

const dadosDaPagina = computed(() => {

    if(props.paginacao !== 'paginada'){
        return props.dados;
    }

    const inicio = (paginaAtual.value - 1) * props.itensPorPagina;

    return props.dados.slice(inicio, inicio + props.itensPorPagina);

});

const slots = useSlots();

/*==================================================================*/
/*================== SELECIONADOS INTERNOS =========================*/
/*==================================================================*/

const selecionadosInternos = computed(() => {

    if(props.selecao === 'unico'){
        return props.selecionado?.[props.itemKey] != null ? [props.selecionado] : [];
    }

    return props.selecionados;

});

/*==================================================================*/
/*================== HEADERS COM ESPACO ============================*/
/*==================================================================*/

const headersComEspaco = computed(() => {

    const headers = [...props.headers];

    if(slots['linha-botoes']){
        headers.unshift({ text: '', value: 'botoes' });
    }

    if(props.selecao !== 'nenhum'){
        headers.unshift({ text: '', value: 'selecao' });
    }

    headers.push({ text: '', value: 'espaco' });

    return headers;

});

/*==================================================================*/
/*================== ALTERA SELECIONADOS ===========================*/
/*==================================================================*/

const alteraSelecionados = (selecionados = []) => {

    if(props.selecao === 'unico'){
        emits('update:selecionado', selecionados?.[0] ?? {});
        return;
    }

    if(props.selecao === 'multiplo'){
        emits('update:selecionados', selecionados);
        return;
    }

};

/*==================================================================*/
/*================== ESTILO DA TABELA ==============================*/
/*==================================================================*/

const estiloTabela = computed(() => {

    const colunasDeDados = props.headers
        .map((header = {}) => header?.width || 'max-content')
        .join(' ');

    const colunaBotoes = slots['linha-botoes'] ? 'max-content ' : '';
    const colunaSelecao = props.selecao !== 'nenhum' ? 'max-content ' : '';

    const colunas = `${colunaSelecao}${colunaBotoes}${colunasDeDados} auto`;

    return { gridTemplateColumns: colunas };

});

/*==================================================================*/
/*================== SLOTS DE ITEM =================================*/
/*==================================================================*/

const slotsDeItem = computed(() =>

    Object.keys(slots).filter(name => name.startsWith('linha-item.'))

);

</script>

<template>

    <div class="c-tabela__wrapper">

        <div class="c-tabela__scroll">

            <div class="c-tabela" :style="estiloTabela">

                <HeaderDaTabela
                    :headers="headersComEspaco"
                    :selecao="props.selecao"
                    :dados="dadosDaPagina"
                    :selecionados="selecionadosInternos"
                    :item-key="props.itemKey"
                    @update:selecionados="alteraSelecionados"
                ></HeaderDaTabela>

                <BodyDaTabela
                    :headers="headersComEspaco"
                    :dados="dadosDaPagina"
                    :disabled="props.disabled"
                    :selecao="props.selecao"
                    :selecionados="selecionadosInternos"
                    :item-key="props.itemKey"
                    @update:selecionados="alteraSelecionados"
                >

                    <template v-if="slots['linha-botoes']" #linha-botoes="slotProps">

                        <slot name="linha-botoes" v-bind="slotProps"></slot>

                    </template>

                    <template
                        v-for="name in slotsDeItem"
                        #[name]="slotProps"
                    >

                        <slot :name="name" v-bind="slotProps ?? {}"></slot>

                    </template>

                </BodyDaTabela>

            </div>

        </div>

        <FooterDaTabela
            v-if="props.paginacao === 'paginada'"
            :pagina-atual="paginaAtual"
            :total-paginas="totalPaginas"
            @update:pagina-atual="(pagina) => paginaAtual = pagina"
        ></FooterDaTabela>

    </div>

</template>

<style scoped>

/*==================================================================*/
/*================== Wrapper da Tabela =============================*/
/*==================================================================*/

.c-tabela__wrapper {
    inline-size: 100%;
    max-inline-size: 100%;
    min-inline-size: 0;
    border: 1px solid #D1D1D1;
    border-radius: 5px;
    overflow: hidden;
}

/*==================================================================*/
/*================== Area com Scroll Horizontal ====================*/
/*==================================================================*/

.c-tabela__scroll {
    inline-size: 100%;
    max-inline-size: 100%;
    min-inline-size: 0;
    overflow: auto;
}

/*==================================================================*/
/*================== Tabela ========================================*/
/*==================================================================*/

.c-tabela {
    display: grid;
    inline-size: max-content;
    min-inline-size: 100%;
}

</style>
